import { useEffect, useState } from "react";
import api from "../lib/api";

const Stat = ({ label, value, hint, testId, accent }) => (
  <div className={`bg-white border ${accent ? "border-klein" : "border-rule"} p-5`} data-testid={testId}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</div>
    <div className={`mt-2 font-serif ${accent ? "text-5xl text-klein" : "text-4xl text-ink"} leading-none`}>{value ?? "—"}</div>
    {hint && <div className="mt-2 text-xs text-quiet">{hint}</div>}
  </div>
);

const FunnelBar = ({ step, max }) => {
  const pct = max > 0 ? Math.round((step.count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-4" data-testid={`funnel-row-${step.key}`}>
      <div className="w-28 font-mono text-[10px] uppercase tracking-wider text-quiet">{step.label}</div>
      <div className="flex-1 bg-paper border border-rule h-7 relative">
        <div className="absolute inset-y-0 left-0 bg-klein" style={{ width: `${pct}%` }} />
        <div className="relative px-3 h-full flex items-center text-xs font-mono text-ink">
          {step.count} · {step.conversion_pct ?? 0}%
        </div>
      </div>
    </div>
  );
};

export default function Reports() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/reports/360");
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load reports");
      }
    })();
  }, []);

  if (err) return <div className="p-8 text-error" data-testid="reports-error">{err}</div>;
  if (!data) return <div className="p-8 text-quiet" data-testid="reports-loading">Loading reports…</div>;

  const tth = data.time_to_hire;
  const maxFunnel = Math.max(...data.funnel.map((s) => s.count), 1);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="reports-page">
      <div className="border-b border-rule pb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Analytics · 360°</div>
        <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Reports.</h1>
        <p className="text-sm text-quiet mt-3">
          Generated {new Date(data.generated_at).toLocaleString()} · {data.totals.candidates} candidates · {data.totals.requisitions} requisitions · {data.totals.hires} hires · {data.totals.joined ?? 0} joined
        </p>
      </div>

      {/* Time to hire hero */}
      <section className="mt-8" data-testid="time-to-hire-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet mb-3">Time to hire</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Stat
            label="Average days to hire"
            value={tth.average_days !== null ? `${tth.average_days}d` : "—"}
            hint={tth.sample_size ? `Across ${tth.sample_size} hire${tth.sample_size === 1 ? "" : "s"}` : "No hires yet"}
            testId="stat-avg-tth"
            accent
          />
          <Stat label="Median" value={tth.median_days !== null ? `${tth.median_days}d` : "—"} testId="stat-median-tth" />
          <Stat label="P90 (slowest 10%)" value={tth.p90_days !== null ? `${tth.p90_days}d` : "—"} testId="stat-p90-tth" />
          <Stat label="Fastest" value={tth.fastest_days !== null ? `${tth.fastest_days}d` : "—"} testId="stat-fastest-tth" />
          <Stat label="Slowest" value={tth.slowest_days !== null ? `${tth.slowest_days}d` : "—"} testId="stat-slowest-tth" />
        </div>
      </section>

      {/* Funnel */}
      <section className="mt-10" data-testid="funnel-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet mb-3">Hiring funnel</div>
        <div className="bg-white border border-rule p-5 space-y-3">
          {data.funnel.map((step) => (
            <FunnelBar key={step.key} step={step} max={maxFunnel} />
          ))}
        </div>
      </section>

      {/* AI screening */}
      <section className="mt-10" data-testid="ai-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet mb-3">AI screening</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Stat label="Candidates scored" value={data.ai_screening.scored_count} testId="stat-ai-scored" />
          <Stat label="Avg overall score" value={data.ai_screening.avg_overall ?? "—"} testId="stat-ai-avg" />
          <Stat label="Avg score · hired" value={data.ai_screening.avg_hired ?? "—"} testId="stat-ai-hired" />
          <Stat label="Avg score · rejected" value={data.ai_screening.avg_rejected ?? "—"} testId="stat-ai-rejected" />
        </div>
      </section>

      {/* Requisition breakdown */}
      <section className="mt-10" data-testid="requisitions-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet mb-3">Requisitions performance</div>
        <div className="bg-white border border-rule overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-paper border-b border-rule">
              <tr>
                <Th>Role</Th>
                <Th>Department</Th>
                <Th>Status</Th>
                <Th align="right">Applicants</Th>
                <Th align="right">Hires</Th>
                <Th align="right">Joined</Th>
                <Th align="right">Rejections</Th>
                <Th align="right">Conversion</Th>
                <Th align="right">Days open</Th>
              </tr>
            </thead>
            <tbody>
              {data.requisitions.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-quiet text-xs">No requisitions yet.</td></tr>
              )}
              {data.requisitions.map((r) => (
                <tr key={r.id} className="border-b border-rule last:border-b-0">
                  <Td>{r.title}</Td>
                  <Td>{r.department}</Td>
                  <Td><span className="font-mono text-[10px] uppercase tracking-wider text-quiet">{r.status}</span></Td>
                  <Td align="right">{r.applicants}</Td>
                  <Td align="right">{r.selected}</Td>
                  <Td align="right">{r.joined ?? 0}</Td>
                  <Td align="right">{r.rejected}</Td>
                  <Td align="right">{r.conversion_pct}%</Td>
                  <Td align="right">{r.days_open !== null ? `${r.days_open}d` : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recruiter performance */}
      <section className="mt-10 mb-10" data-testid="recruiters-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet mb-3">Recruiter performance</div>
        <div className="bg-white border border-rule overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-paper border-b border-rule">
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th align="right">Interviews scheduled</Th>
                <Th align="right">Hires</Th>
                <Th align="right">Rejections</Th>
              </tr>
            </thead>
            <tbody>
              {data.recruiters.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-quiet text-xs">No recruiter activity yet.</td></tr>
              )}
              {data.recruiters.map((u) => (
                <tr key={u.email} className="border-b border-rule last:border-b-0">
                  <Td>{u.name}</Td>
                  <Td>{u.email}</Td>
                  <Td><span className="font-mono text-[10px] uppercase tracking-wider text-quiet">{u.role}</span></Td>
                  <Td align="right">{u.interviews_scheduled}</Td>
                  <Td align="right">{u.hires}</Td>
                  <Td align="right">{u.rejections}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const Th = ({ children, align = "left" }) => (
  <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-quiet text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left" }) => (
  <td className={`px-4 py-3 text-ink text-${align}`}>{children}</td>
);
